import React, { useState } from 'react';
import { Button, Spin, Tabs, Upload } from 'antd';
import { toast } from 'react-toastify';
import { FloatingLabelInput, FloatingLabelTextArea } from '../elements/floatingLabelInput/FloatingLabelInput';
import TranslateButton from '../elements/TranslateButton/TranslateButton';
import ToggleSwitch from '../elements/toggleSwitch/ToggleSwitch';
import { useTranslateFields } from '../../hooks/useTranslateFields';
import styles from '../../styles/mobileInfoPage.module.scss';
import uploadStyles from '../../styles/editAnnoucement.module.scss';

const LANG_SECTIONS = [
  { key: 'sr', label: 'Srpski' },
  { key: 'hu', label: 'Mađarski' },
  { key: 'en', label: 'Engleski' },
];

const LANGS = ['sr', 'hu', 'en'];
const LANG_LABELS = { sr: 'srpskom', hu: 'mađarskom', en: 'engleskom' };

export const EMPTY_CATEGORY_FORM = {
  slug: '',
  menu_order: '',
  image: null,
  showOnHome: false,
  isPackages: false,
  multiLanguage: {
    sr: { name: '', description: '' },
    hu: { name: '', description: '' },
    en: { name: '', description: '' },
  },
};

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

// Mirrors the backend's validateCategoryMultiLanguageContent
// (src/services/category.service.ts): name required for all 3 languages,
// description optional but all-or-nothing across languages.
const validate = (formData) => {
  if (!formData.slug?.trim()) {
    toast.error('Slug je obavezan');
    return false;
  }

  for (const lang of LANGS) {
    if (!formData.multiLanguage[lang].name?.trim()) {
      toast.error(`Naziv na ${LANG_LABELS[lang]} je obavezan`);
      return false;
    }
  }

  const filledDescriptions = LANGS.filter((l) => formData.multiLanguage[l].description?.trim()).length;
  if (filledDescriptions > 0 && filledDescriptions < LANGS.length) {
    toast.error('Opis mora biti popunjen za sva tri jezika ili ni za jedan');
    return false;
  }

  return true;
};

const buildPayload = (formData) => ({
  slug: formData.slug.trim(),
  menu_order: formData.menu_order !== '' ? Number(formData.menu_order) : undefined,
  image: formData.image ? { src: formData.image.src, name: formData.image.name, alt: formData.image.alt } : undefined,
  showOnHome: formData.showOnHome,
  // Off the home row means no position; re-enabling puts it last. When on,
  // the position is managed by HomeCategoryOrder, so leave it untouched.
  homeOrder: formData.showOnHome ? undefined : null,
  isPackages: formData.isPackages,
  multiLanguage: formData.multiLanguage,
});

// `initialForm` must already be resolved (non-null) by the time this mounts
// for mode="edit" — the parent page keeps its own loading gate, same as
// ProductForm/disease/protocol edit pages do before their forms mount.
export default function CategoryForm({ mode, initialForm, saving, onSubmit }) {
  const [formData, setFormData] = useState(initialForm || EMPTY_CATEGORY_FORM);
  const [uploadingImage, setUploadingImage] = useState(false);

  const setLangField = (lang, field, value) =>
    setFormData((prev) => ({
      ...prev,
      multiLanguage: { ...prev.multiLanguage, [lang]: { ...prev.multiLanguage[lang], [field]: value } },
    }));

  const handleImageUpload = async (file) => {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Dozvoljen je samo JPG, PNG ili WebP format');
      return false;
    }
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('Slika je prevelika. Maksimalna veličina je 5MB');
      return false;
    }

    setUploadingImage(true);
    try {
      const base64 = await fileToBase64(file);
      setFormData((prev) => ({ ...prev, image: { src: base64, name: file.name, alt: '' } }));
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Greška pri učitavanju slike');
    } finally {
      setUploadingImage(false);
    }
    return false; // prevent antd's default upload behavior
  };

  const handleDeleteImage = () => setFormData((prev) => ({ ...prev, image: null }));

  const handleSubmit = () => {
    if (!validate(formData)) return;
    onSubmit(buildPayload(formData));
  };

  const { translating, translate } = useTranslateFields();

  const handleTranslate = async () => {
    const sr = formData.multiLanguage.sr;
    if (!sr.name?.trim()) {
      toast.error('Popunite naziv na srpskom pre prevođenja');
      return;
    }

    const fields = { name: sr.name };
    if (sr.description?.trim()) {
      fields.description = sr.description;
    }

    const result = await translate(fields);
    if (!result) return;

    for (const lang of ['hu', 'en']) {
      const translated = result[lang];
      setLangField(lang, 'name', translated.name);
      if (translated.description !== undefined) {
        setLangField(lang, 'description', translated.description);
      }
    }

    toast.success('Prevod je uspešno primenjen');
  };

  const langTabItems = LANG_SECTIONS.map(({ key, label }) => ({
    key,
    label,
    children: (
      <div className={styles.fieldStack}>
        <FloatingLabelInput
          label={`Naziv — ${label}`}
          value={formData.multiLanguage[key].name}
          onChange={(e) => setLangField(key, 'name', e.target.value)}
        />
        <FloatingLabelTextArea
          label={`Opis — ${label} (opciono)`}
          value={formData.multiLanguage[key].description}
          onChange={(e) => setLangField(key, 'description', e.target.value)}
          rows={3}
        />
      </div>
    ),
  }));

  return (
    <div>
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Osnovni podaci</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px' }}>
          <FloatingLabelInput
            label="Slug"
            value={formData.slug}
            onChange={(e) => setFormData((prev) => ({ ...prev, slug: e.target.value }))}
          />
          {/*<FloatingLabelInput
            label="Redosled prikaza (opciono)"
            type="number"
            value={formData.menu_order}
            onChange={(e) => setFormData((prev) => ({ ...prev, menu_order: e.target.value }))}
          />*/}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '20px' }}>
          <ToggleSwitch
            checked={formData.showOnHome || false}
            onChange={(e) => setFormData((prev) => ({ ...prev, showOnHome: e.target.checked }))}
            label="Prikaži na početnoj prodavnice"
            tooltip="Kategorija se prikazuje kao brzi čip na vrhu ekrana prodavnice u mobilnoj aplikaciji."
          />
          <ToggleSwitch
            checked={formData.isPackages || false}
            onChange={(e) => setFormData((prev) => ({ ...prev, isPackages: e.target.checked }))}
            label="Kategorija paketa (Mix 4 paket)"
            tooltip="Oznaka za mobilnu aplikaciju — u ovoj kategoriji se prikazuje virtuelna kartica za kreiranje Mix 4 paketa."
          />
        </div>
      </section>

      <section className={styles.section}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 className={styles.sectionTitle}>Naziv i opis</h3>
          <TranslateButton onClick={handleTranslate} loading={translating} />
        </div>
        <Tabs items={langTabItems} className="lang_tabs" />
      </section>

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Slika (opciono)</h3>
        <div className={uploadStyles.photoUploadContainer}>
          <div className={uploadStyles.previewWrapper}>
            {formData.image ? (
              <>
                <img src={formData.image.src} alt={formData.image.alt || formData.image.name} className={uploadStyles.imagePreview} />
                <button className={uploadStyles.deleteBtn} onClick={handleDeleteImage} type="button" disabled={uploadingImage}>
                  <span className={uploadStyles.trashIcon}>🗑️</span>
                </button>
              </>
            ) : (
              <img src="/logo.svg" alt="Placeholder" className={uploadStyles.imagePreview} />
            )}
          </div>
          <Upload
            accept="image/jpeg,image/jpg,image/png,image/webp"
            beforeUpload={handleImageUpload}
            showUploadList={false}
            disabled={uploadingImage}
            className={uploadStyles.uploadBoxWrapper}
          >
            <div className={uploadStyles.uploadBox}>
              <div className={uploadStyles.uploadContent}>
                <div className={uploadStyles.cloudIconContainer}>
                  {uploadingImage ? <Spin /> : <span className={uploadStyles.cloudIcon}>☁️</span>}
                </div>
                <p className={uploadStyles.uploadText}>
                  <span className={uploadStyles.highlight}>
                    {uploadingImage ? 'Učitavanje...' : 'Kliknite za otpremanje slike'}
                  </span>{' '}
                  ili prevuci i ispusti
                </p>
                <small className={uploadStyles.fileTypes}>.JPG, .PNG, .WebP (max. 5MB)</small>
              </div>
            </div>
          </Upload>
        </div>
      </section>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24 }}>
        <Button className="btn-primary" type="primary" onClick={handleSubmit} loading={saving}>
          {mode === 'create' ? 'Kreiraj kategoriju' : 'Sačuvaj izmene'}
        </Button>
      </div>
    </div>
  );
}
